import { useEffect, useRef, useState, type ReactNode } from 'react'

interface LazyMountProps {
  children: ReactNode
  /**
   * Rendered until the wrapper scrolls into view (or near it). Should
   * have the same outer dimensions as `children` to avoid layout shift
   * when the swap happens.
   */
  placeholder?: ReactNode
  /**
   * IntersectionObserver `rootMargin`. Defaults to a generous `400px`
   * so content pre-mounts a screen ahead of the user — keeps scrolling
   * smooth and avoids visible "popping" of skeletons.
   */
  rootMargin?: string
  className?: string
}

/**
 * Defers rendering of expensive children until the wrapper element is
 * near the viewport, swapping a lightweight placeholder in until then.
 *
 * Once a child has mounted we keep it mounted for the lifetime of the
 * wrapper — scrolling away does NOT unmount, so component state (form
 * inputs, lazy-loaded media, modals) is preserved.
 *
 * Falls back to eager rendering on environments without
 * `IntersectionObserver` (very old browsers, JSDOM tests).
 */
export function LazyMount({
  children,
  placeholder = null,
  rootMargin = '400px',
  className,
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isVisible) return
    const el = ref.current
    if (!el) return

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
            break
          }
        }
      },
      { rootMargin },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isVisible, rootMargin])

  // The wrapper must be a real block-level element so IntersectionObserver
  // has a non-zero bounding box to observe. If the caller wants the wrapper
  // to disappear from layout once the children mount, they can layer that
  // via the className override.
  return (
    <div ref={ref} className={className}>
      {isVisible ? children : placeholder}
    </div>
  )
}
